import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useAppointment } from "@/components/AppointmentModal";
import { CheckCircle2, XCircle, Eye, RotateCcw, ArrowRight } from "lucide-react";

// ─── Plate definitions (ordered easy → hard) ───
const PLATES = [
  // Level 1: дуже легко — велика різниця контрастів
  { id: 1, number: 12, options: ["12", "21", "15", "Нічого"], correct: "12", deficient: null, difficulty: 1 },
  // Level 2: легко
  { id: 2, number: 8, options: ["8", "3", "6", "Нічого"], correct: "8", deficient: "3", difficulty: 2 },
  // Level 3: середньо
  { id: 3, number: 29, options: ["29", "70", "27", "Нічого"], correct: "29", deficient: "70", difficulty: 3 },
  // Level 4: важко
  { id: 4, number: 6, options: ["6", "9", "5", "Нічого"], correct: "6", deficient: "Нічого", difficulty: 4 },
  // Level 5: дуже важко
  { id: 5, number: 57, options: ["57", "35", "67", "Нічого"], correct: "57", deficient: "35", difficulty: 5 },
  // Level 6: найважче
  { id: 6, number: 5, options: ["5", "2", "8", "Нічого"], correct: "5", deficient: "Нічого", difficulty: 6 },
];

// Color palettes by difficulty — from high contrast (easy) to low contrast (hard)
const PALETTES = {
  1: {
    bg: ["#E8590A", "#D44A00", "#F07030", "#F0A060", "#C84020", "#FF8040"],
    num: ["#3D5A1C", "#4A6020", "#2F4715", "#5A7028", "#3A521B", "#4D6422"],
  },
  2: {
    bg: ["#E8590A", "#D44A00", "#F07030", "#F0A060", "#C84020", "#FF8040"],
    num: ["#6B7A2A", "#7A8235", "#5A6020", "#8A9040", "#4F5818", "#6F7C2D"],
  },
  3: {
    bg: ["#E8590A", "#D44A00", "#E07030", "#E0A060", "#C84020", "#EF7530"],
    num: ["#8A7040", "#967A48", "#7A6030", "#9A8050", "#876B3E", "#937545"],
  },
  4: {
    bg: ["#D8651F", "#C45A18", "#DC7A35", "#E09060", "#BF5530", "#D87045"],
    num: ["#A0763A", "#AD8045", "#956D35", "#B08B55", "#9C7240", "#A77D48"],
  },
  5: {
    bg: ["#C8651F", "#B65A20", "#CC7035", "#D08560", "#B05530", "#C36545"],
    num: ["#B07840", "#BB8045", "#A57238", "#C08B55", "#AD7842", "#B68048"],
  },
  6: {
    bg: ["#B86528", "#A55A25", "#BC6F35", "#BC7D50", "#A55530", "#B36545"],
    num: ["#B57842", "#BB8050", "#AD7A3F", "#C28856", "#B27B48", "#B7804C"],
  },
};

// ─── Procedural plate generator ───
function drawIshiharaPlate(canvas, number, difficulty = 1) {
  const palette = PALETTES[difficulty] || PALETTES[1];
  const BG_COLORS = palette.bg;
  const NUM_COLORS = palette.num;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const sizeCSS = canvas.clientWidth;
  const size = sizeCSS * dpr;
  canvas.width = size;
  canvas.height = size;
  ctx.scale(dpr, dpr);

  const w = sizeCSS;
  const h = sizeCSS;
  const cx = w / 2;
  const cy = h / 2;
  const radius = w / 2 - 4;

  // Background (subtle transparent outside the circle)
  ctx.clearRect(0, 0, w, h);

  // Create mask canvas with number
  const mask = document.createElement("canvas");
  mask.width = w;
  mask.height = h;
  const mctx = mask.getContext("2d");
  mctx.fillStyle = "#ffffff";
  mctx.fillRect(0, 0, w, h);
  mctx.fillStyle = "#000000";
  mctx.font = `bold ${Math.floor(h * 0.55)}px Arial, sans-serif`;
  mctx.textAlign = "center";
  mctx.textBaseline = "middle";
  mctx.fillText(String(number), cx, cy + h * 0.02);
  const maskData = mctx.getImageData(0, 0, w, h).data;

  // Deterministic-ish random generator for more organic look (seeded optional)
  const rand = Math.random;

  // Clip the entire plate to circle
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  // Fill plate with a very subtle off-white/cream tone to match Ishihara paper
  ctx.fillStyle = "#F7F1EA";
  ctx.fillRect(0, 0, w, h);

  // Draw dots via simple rejection sampling on a grid of candidate positions
  const placed = []; // {x,y,r}
  const minDist = 1.5; // minimum gap between dot edges

  const attempts = Math.floor(w * w * 0.9);
  for (let i = 0; i < attempts; i++) {
    // Bias dot sizes: many small, few medium
    const r = 3 + Math.pow(rand(), 1.6) * 6; // 3..9
    const x = rand() * w;
    const y = rand() * h;

    // Must be inside the circle with small padding
    const dc = Math.hypot(x - cx, y - cy);
    if (dc > radius - r - 1) continue;

    // Reject heavy overlap (check nearby placed dots in a small window)
    let collides = false;
    for (let j = placed.length - 1; j >= 0 && j >= placed.length - 40; j--) {
      const p = placed[j];
      if (Math.hypot(p.x - x, p.y - y) < p.r + r + minDist) {
        collides = true;
        break;
      }
    }
    if (collides) continue;

    // Sample mask
    const ix = Math.max(0, Math.min(w - 1, Math.floor(x)));
    const iy = Math.max(0, Math.min(h - 1, Math.floor(y)));
    const pix = (iy * w + ix) * 4;
    const isNumber = maskData[pix] < 128;
    const palette = isNumber ? NUM_COLORS : BG_COLORS;
    const color = palette[Math.floor(rand() * palette.length)];

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();

    placed.push({ x, y, r });
    if (placed.length > 2800) break;
  }

  ctx.restore();

  // Subtle ring around the plate
  ctx.strokeStyle = "rgba(0,0,0,0.06)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
}

// ─── Single Plate component ───
const Plate = ({ number, difficulty }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (canvasRef.current) drawIshiharaPlate(canvasRef.current, number, difficulty);
  }, [number, difficulty]);
  return (
    <div className="relative mx-auto aspect-square w-[280px] sm:w-[340px] lg:w-[380px]">
      <canvas ref={canvasRef} className="w-full h-full" data-testid={`ishihara-plate-${number}`} />
    </div>
  );
};

// ─── Intro Screen ───
const IntroScreen = ({ onStart }) => (
  <motion.div
    key="intro"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.4 }}
    className="text-center py-2"
  >
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FFF3ED] mb-5">
      <Eye className="w-7 h-7 text-[#E8590A]" strokeWidth={1.75} />
    </div>
    <p className="text-base lg:text-lg text-[#333] leading-snug max-w-md mx-auto mb-6">
      Чи бачите ви кольори так, як думаєте?
    </p>
    <button
      onClick={onStart}
      className="bg-[#E8590A] text-white px-7 py-3.5 rounded-full hover:bg-[#D14D08] transition-all duration-300 font-semibold text-base"
      data-testid="ishihara-start-btn"
    >
      Почати тест
    </button>
  </motion.div>
);

// ─── Result Screen ───
const ResultScreen = ({ answers, onReset }) => {
  const { open: openAppointment } = useAppointment();
  // Score
  let correct = 0;
  let deficient = 0;
  PLATES.forEach((p, i) => {
    const ans = answers[i];
    if (ans === p.correct) correct++;
    else if (p.deficient && ans === p.deficient) deficient++;
  });

  // Difficulty-aware scoring: easy plates (1-2) must be solved correctly;
  // harder plates (5-6) — slight tolerance.
  const easyCorrect = [0, 1].filter((i) => answers[i] === PLATES[i].correct).length;
  const mediumCorrect = [2, 3].filter((i) => answers[i] === PLATES[i].correct).length;

  let title, desc, tone;
  if (easyCorrect === 2 && mediumCorrect >= 1 && deficient === 0) {
    title = "Чудово! Кольоровий зір у нормі";
    desc = "Ви правильно впізнали ключові пластини. Але не забувайте про регулярні профілактичні огляди кожні 1–2 роки.";
    tone = "normal";
  } else if (deficient >= 2 || (deficient >= 1 && easyCorrect < 2)) {
    title = "Можливо, є червоно-зелене порушення";
    desc = "Ваші відповіді свідчать про ознаки дефіциту сприйняття червоного/зеленого кольору. Рекомендуємо записатись на консультацію до офтальмолога для точного обстеження.";
    tone = "deficiency";
  } else {
    title = "Рекомендуємо консультацію";
    desc = "Деякі відповіді нетипові для нормального кольорового зору. Для точного діагнозу необхідне професійне обстеження — цей тест лише орієнтовний.";
    tone = "unclear";
  }

  return (
    <motion.div
      key="result"
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="text-center"
    >
      <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 ${tone === "normal" ? "bg-[#E8F7EC]" : "bg-[#FFF3ED]"}`}>
        {tone === "normal" ? (
          <CheckCircle2 className="w-10 h-10 text-[#16A34A]" strokeWidth={1.75} />
        ) : (
          <Eye className="w-10 h-10 text-[#E8590A]" strokeWidth={1.75} />
        )}
      </div>
      <h3 className="text-2xl lg:text-3xl font-semibold text-[#333] leading-tight mb-4">{title}</h3>
      <p className="text-[#666] text-base leading-relaxed max-w-md mx-auto mb-8">{desc}</p>

      {/* Summary */}
      <div className="flex justify-center gap-6 mb-10 text-sm">
        <div className="flex flex-col items-center">
          <span className="text-2xl font-bold text-[#16A34A]">{correct}</span>
          <span className="text-[#808080]">правильних</span>
        </div>
        <div className="w-px bg-[#E5E5E5]" />
        <div className="flex flex-col items-center">
          <span className="text-2xl font-bold text-[#E8590A]">{PLATES.length - correct}</span>
          <span className="text-[#808080]">неточних</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={openAppointment}
          className="inline-flex items-center justify-center gap-2 bg-[#E8590A] text-white px-7 py-3.5 rounded-full hover:bg-[#D14D08] transition-colors font-semibold text-base"
          data-testid="ishihara-appointment-btn"
        >
          Записатись на консультацію <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={onReset}
          className="inline-flex items-center justify-center gap-2 text-[#666] hover:text-[#E8590A] font-medium transition-colors text-base"
          data-testid="ishihara-reset-btn"
        >
          <RotateCcw className="w-4 h-4" /> Пройти ще раз
        </button>
      </div>
    </motion.div>
  );
};

// ─── Plate Screen ───
const PlateScreen = ({ plate, stepIndex, total, onAnswer }) => {
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    setSelected(null);
    setLocked(false);
  }, [plate.id]);

  const handleClick = (opt) => {
    if (locked) return;
    setSelected(opt);
    setLocked(true);
    setTimeout(() => onAnswer(opt), 650);
  };

  return (
    <motion.div
      key={`plate-${plate.id}`}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.35 }}
    >
      <div className="text-center mb-6">
        <p className="text-xs font-medium tracking-wider uppercase text-[#E8590A] mb-2">
          Пластина {stepIndex + 1} з {total}
        </p>
        <h3 className="text-lg lg:text-xl font-semibold text-[#333]">Яку цифру ви бачите?</h3>
      </div>

      <Plate number={plate.number} difficulty={plate.difficulty} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
        {plate.options.map((opt) => {
          const isSelected = selected === opt;
          const isCorrect = locked && opt === plate.correct;
          const isWrong = locked && isSelected && opt !== plate.correct;

          let style = "border-[#E5E5E5] text-[#333] hover:border-[#E8590A] hover:bg-[#FFF3ED]";
          if (isCorrect) style = "border-[#16A34A] bg-[#E8F7EC] text-[#16A34A]";
          else if (isWrong) style = "border-[#DC2626] bg-[#FDECEC] text-[#DC2626]";
          else if (locked) style = "border-[#E5E5E5] text-[#CFCFCF]";

          return (
            <button
              key={opt}
              onClick={() => handleClick(opt)}
              disabled={locked}
              className={`relative h-14 sm:h-16 rounded-xl border-2 font-semibold text-lg transition-all duration-200 ${style}`}
              data-testid={`ishihara-option-${plate.id}-${opt}`}
            >
              {opt}
              {isCorrect && <CheckCircle2 className="absolute top-2 right-2 w-4 h-4" />}
              {isWrong && <XCircle className="absolute top-2 right-2 w-4 h-4" />}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

// ─── Reusable Card (no section wrapper) ───
export const IshiharaTest = () => {
  const [phase, setPhase] = useState("test"); // test | result
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const reset = () => { setAnswers([]); setStep(0); setPhase("test"); };

  const handleAnswer = (opt) => {
    const next = [...answers, opt];
    setAnswers(next);
    if (step + 1 < PLATES.length) setStep(step + 1);
    else setPhase("result");
  };

  const progress = phase === "result" ? 100 : (step / PLATES.length) * 100;

  return (
    <div className="bg-white rounded-3xl border border-[#EDEDED] shadow-[0_12px_40px_-16px_rgba(0,0,0,0.08)] p-5 sm:p-8 relative overflow-hidden" data-testid="ishihara-test">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#F5F5F5]">
        <motion.div
          className="h-full bg-[#E8590A]"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      <AnimatePresence mode="wait">
        {phase === "test" && (
          <PlateScreen
            plate={PLATES[step]}
            stepIndex={step}
            total={PLATES.length}
            onAnswer={handleAnswer}
          />
        )}
        {phase === "result" && <ResultScreen answers={answers} onReset={reset} />}
      </AnimatePresence>
    </div>
  );
};

// ─── Main Test Section (homepage) ───
export const IshiharaTestSection = () => {
  return (
    <section className="py-14 md:py-20 bg-[#F5F5F5]" data-testid="ishihara-test-section" id="self-test">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-6 lg:mb-8">
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold text-[#333] leading-[1.1] tracking-tight">
              Пройдіть <span className="text-[#E8590A]">безкоштовний тест</span> прямо зараз
            </h2>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <IshiharaTest />
        </ScrollReveal>
      </div>
    </section>
  );
};
